import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import '../../v2-theme.css';
import './AuthPages.css';
import './RegisterPages.css';

/**
 * Worker Registration Page
 * Matches worker-register.html exactly
 * Complex multi-section form with feature gating
 */
const WorkerRegister = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [formData, setFormData] = useState({
    // Personal Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: 'United States',

    // Communication Preferences
    commPreferences: {
      email: false,  // Workers must explicitly opt-in
      sms: false  // Will be required
    },

    // Work Experience
    experience: '',
    positions: [],  // Limited to 1 for free account
    availability: [],

    // Account Setup
    password: '',
    confirmPassword: '',
    acceptedTerms: false
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const POSITIONS = [
    { value: 'dishwasher', label: 'Dishwasher' },
    { value: 'prep-cook', label: 'Prep Cook' },
    { value: 'line-cook', label: 'Line Cook' },
    { value: 'sous-chef', label: 'Sous Chef' },
    { value: 'head-chef', label: 'Head Chef' },
    { value: 'event-cook', label: 'Event Cook' },
    { value: 'banquet', label: 'Banquet' },
    { value: 'bartender', label: 'Bartender' },
    { value: 'barback', label: 'Barback' }
  ];

  const AVAILABILITY = [
    { value: 'weekday-am', label: 'Weekday Mornings (6AM-2PM)' },
    { value: 'weekday-pm', label: 'Weekday Evenings (2PM-10PM)' },
    { value: 'weekend-am', label: 'Weekend Mornings (6AM-2PM)' },
    { value: 'weekend-pm', label: 'Weekend Evenings (2PM-10PM)' },
    { value: 'late-night', label: 'Late Night (10PM-6AM)' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCommPrefChange = (pref) => {
    setFormData(prev => ({
      ...prev,
      commPreferences: {
        ...prev.commPreferences,
        [pref]: !prev.commPreferences[pref]
      }
    }));
  };

  const handlePositionChange = (position) => {
    setFormData(prev => {
      const newPositions = prev.positions.includes(position)
        ? prev.positions.filter(p => p !== position)
        : [...prev.positions, position];

      // Free account: limit to 1 position
      if (newPositions.length > 1) {
        alert('Free account is limited to one position. Upgrade to Professional to select multiple positions.');
        return prev;
      }

      return { ...prev, positions: newPositions };
    });
  };

  const handleAvailabilityChange = (avail) => {
    setFormData(prev => ({
      ...prev,
      availability: prev.availability.includes(avail)
        ? prev.availability.filter(a => a !== avail)
        : [...prev.availability, avail]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.commPreferences.sms) {
      setError('SMS communication is required for shift notifications.');
      return;
    }

    if (!formData.experience) {
      setError('Please select your years of experience.');
      return;
    }

    if (formData.positions.length === 0) {
      setError('Please select at least one position.');
      return;
    }

    if (formData.availability.length === 0) {
      setError('Please select at least one availability timeframe.');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!formData.acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy.');
      return;
    }

    setLoading(true);

    try {
      // Sign up user
      const result = await signUp(formData.email, formData.password, 'worker');

      if (!result.success) {
        setError(result.error || 'Registration failed. Please try again.');
        setLoading(false);
        return;
      }

      // Create worker profile
      await api.post('/profiles/worker', {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        years_experience: formData.experience,
        positions: formData.positions,
        availability: formData.availability,
        communication_preferences: formData.commPreferences
      });

      // Redirect to worker dashboard
      navigate('/dashboard/worker');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || err.message || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="auth-page register-page">
      <div className="auth-container register-container">
        <header className="auth-header">
          <div className="auth-logo-title">
            <img src="/hotf-web-icon.png" alt="Hands On The Fly" className="auth-logo" />
            <h1>Hands On The Fly</h1>
          </div>
        </header>

        <Link to="/" className="auth-back-link">← Back to Home</Link>

        <h2 className="auth-heading">Worker Registration</h2>
        <p className="auth-subtitle">Kitchen Staff</p>

        <form className="auth-form register-form" onSubmit={handleSubmit}>
          {error && <div className="auth-error-message">{error}</div>}

          {/* Personal Information */}
          <div className="register-section">
            <h3 className="register-section-title">Personal Information</h3>

            <div className="register-form-row">
              <div className="auth-form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="auth-form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="auth-form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="auth-form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="register-form-row">
              <div className="auth-form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="auth-form-group">
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="auth-form-group">
              <label>Communication Preferences</label>
              <div className="register-checkbox-group">
                <label className="register-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.commPreferences.email}
                    onChange={() => handleCommPrefChange('email')}
                    disabled={loading}
                  />
                  <span>Email</span>
                </label>
                <label className="register-checkbox-label required-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.commPreferences.sms}
                    onChange={() => handleCommPrefChange('sms')}
                    disabled={loading}
                  />
                  <span>SMS (Required)</span>
                </label>
              </div>
            </div>
          </div>

          {/* Work Experience */}
          <div className="register-section">
            <h3 className="register-section-title">Work Experience</h3>

            <div className="auth-form-group">
              <label htmlFor="experience">Years of Experience</label>
              <select
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                required
                disabled={loading}
              >
                <option value="">Select experience level</option>
                <option value="0-1">0-1 years</option>
                <option value="1-3">1-3 years</option>
                <option value="3-5">3-5 years</option>
                <option value="5-10">5-10 years</option>
                <option value="10+">10+ years</option>
              </select>
            </div>

            <div className="auth-form-group">
              <label>Positions</label>
              <p className="register-note">
                Free account: Select one position. Upgrade to Professional for multiple positions.
              </p>
              <div className="register-checkbox-group">
                {POSITIONS.map(pos => (
                  <label key={pos.value} className="register-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.positions.includes(pos.value)}
                      onChange={() => handlePositionChange(pos.value)}
                      disabled={loading}
                    />
                    <span>{pos.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="auth-form-group">
              <label>Availability</label>
              <div className="register-checkbox-group">
                {AVAILABILITY.map(avail => (
                  <label key={avail.value} className="register-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.availability.includes(avail.value)}
                      onChange={() => handleAvailabilityChange(avail.value)}
                      disabled={loading}
                    />
                    <span>{avail.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Account Setup */}
          <div className="register-section">
            <h3 className="register-section-title">Account Setup</h3>

            <div className="auth-form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="8"
                disabled={loading}
              />
              <p className="auth-helper-text">Minimum 8 characters</p>
            </div>

            <div className="auth-form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                minLength="8"
                disabled={loading}
              />
            </div>

            <div className="register-terms">
              <label className="register-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.acceptedTerms}
                  onChange={(e) => setFormData(prev => ({ ...prev, acceptedTerms: e.target.checked }))}
                  disabled={loading}
                />
                <span>
                  I accept the{' '}
                  <Link to="/terms" target="_blank">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank">Privacy Policy</Link>
                </span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="auth-submit-btn"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Register'}
          </button>
        </form>

        <div className="auth-register-link">
          <p>Already have an account?</p>
          <button
            className="auth-register-btn"
            onClick={() => navigate('/worker-login')}
            disabled={loading}
          >
            Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkerRegister;
